import React, { useEffect, useState } from "react";
import { Alert, Button, Container } from "react-bootstrap";
import { createPortal } from "react-dom";
import "./ImpersonationBanner.css";
import { useStopImpersonate, useUser } from "services/account";

const ImpersonationBannerPortal: React.FC = () => {
  const { user } = useUser();
  const { mutateAsync: stopImpersonation } = useStopImpersonate();

  const [bannerContainer, setBannerContainer] = useState<HTMLElement | null>(
    null
  );

  // Criar e gerenciar o elemento container para o portal
  useEffect(() => {
    // Se o usuário estiver em modo de personificação
    if (user?.impersonating && user?.originalUser) {
      // Criar o container se ainda não existir
      if (!document.getElementById("impersonation-banner-container")) {
        const div = document.createElement("div");
        div.id = "impersonation-banner-container";
        document.body.prepend(div); // Adiciona no topo do body
        setBannerContainer(div);
      } else {
        setBannerContainer(
          document.getElementById("impersonation-banner-container")
        );
      }

      // Ajustar a posição de todos os elementos fixed-top
      const fixedElements = document.querySelectorAll(".fixed-top, .navbar");
      fixedElements.forEach((el: any) => {
        el.style.marginTop = "48px"; // Altura do banner
      });

      // Ajustar o padding-top do body para compensar o banner
      document.body.style.paddingTop = "48px";
    } else {
      // Se não estiver em personificação, remover o container se existir
      const container = document.getElementById(
        "impersonation-banner-container"
      );
      if (container) {
        document.body.removeChild(container);
        setBannerContainer(null);
      }

      // Restaurar a posição dos elementos fixed-top
      const fixedElements = document.querySelectorAll(".fixed-top, .navbar");
      fixedElements.forEach((el: any) => {
        el.style.marginTop = "0";
      });

      // Restaurar o padding do body
      document.body.style.paddingTop = "0";
    }

    // Cleanup
    return () => {
      const container = document.getElementById(
        "impersonation-banner-container"
      );
      if (container) {
        document.body.removeChild(container);
      }

      // Restaurar a posição dos elementos fixed-top
      const fixedElements = document.querySelectorAll(".fixed-top, .navbar");
      fixedElements.forEach((el: any) => {
        el.style.marginTop = "0";
      });

      // Restaurar o padding do body
      document.body.style.paddingTop = "0";
    };
  }, [user?.impersonating]);

  // Não exibir o banner se não estiver personificando
  if (!user?.impersonating || !user?.originalUser || !bannerContainer) {
    return null;
  }

  // Usar createPortal para renderizar o banner diretamente no container
  return createPortal(
    <Alert
      variant="subtle-warning"
      className="impersonation-banner mb-0 rounded-0 py-2"
    >
      <Container
        fluid
        className="d-flex flex-wrap justify-content-between align-items-center"
      >
        <div className="d-flex flex-wrap align-items-center">
          <div className="me-2">
            <i className="fas fa-user-secret me-2"></i>
            <strong>Modo de personificação:</strong>
          </div>
          <div>
            Você está acessando como <strong>{user.nome}</strong>{" "}
            <span className="text-muted">({user.email})</span>
          </div>
          <div className="mx-2 d-none d-md-block">•</div>
          <div>
            Usuário original: <strong>{user.originalUser.nome}</strong>{" "}
            <span className="text-muted">({user.originalUser.email})</span>
          </div>
        </div>
        <Button
          variant="outline-dark"
          size="sm"
          onClick={async () => {
            await stopImpersonation();
          }}
          className="mt-2 mt-sm-0"
        >
          <i className="fas fa-sign-out-alt me-1"></i>
          Encerrar Personificação
        </Button>
      </Container>
    </Alert>,
    bannerContainer
  );
};

export default ImpersonationBannerPortal;
